import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Criterion, HiringMatrix, MatrixFilter } from "@/types";
import { api } from "@/lib/api";
import { FilterEditor } from "../common/FilterEditor";

interface Props {
  matrix: HiringMatrix;
  onDelete: (id: number) => void;
  onUpdated: () => void;
  defaultOpen?: boolean;
}

export function MatrixCard({ matrix, onDelete, onUpdated, defaultOpen }: Props) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(defaultOpen ?? false);
  const [name, setName] = useState(matrix.name);
  const [empType, setEmpType] = useState<"regular" | "contractual" | "intern">(
    matrix.employment_type ?? "regular"
  );
  const [criteria, setCriteria] = useState<Criterion[]>(matrix.criteria);
  const [filters, setFilters] = useState<MatrixFilter[]>(matrix.filters ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalWeight = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  const cancel = () => {
    setName(matrix.name);
    setEmpType(matrix.employment_type ?? "regular");
    setCriteria(matrix.criteria);
    setFilters(matrix.filters ?? []);
    setError(null);
    setEditing(false);
    navigate("/matrices");
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) return setError("Matrix name is required");
    if (criteria.some((c) => !c.name.trim() || !c.requirement.trim())) {
      return setError("Each criterion needs a name and a requirement");
    }
    if (totalWeight <= 0) return setError("Weights must sum to > 0");

    setSaving(true);
    try {
      const res = await api.updateMatrix(matrix.id, {
        name: name.trim(),
        employment_type: empType,
        criteria,
        filters,
      });
      setEditing(false);
      navigate("/matrices");
      if (res.rescreened_count > 0) {
        alert(
          `Matrix saved. Re-evaluating ${res.rescreened_count} candidate${res.rescreened_count === 1 ? "" : "s"} in the background — refresh the candidates page in a minute to see updated scores.`
        );
      }
      onUpdated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const matrixFilters = matrix.filters ?? [];
    const currentEmpType = matrix.employment_type ?? "regular";
    return (
      <div className="rounded-badge border border-surface-secondary bg-surface-secondary p-4 py-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-near-black">{matrix.name}</span>
              {currentEmpType !== "regular" && (
                <span
                  className={`rounded-badge px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    currentEmpType === "intern"
                      ? "bg-sky-100 text-sky-700"
                      : "bg-violet-100 text-violet-700"
                  }`}
                >
                  {currentEmpType}
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-text-secondary">
              {matrix.criteria.length} criteria ·{" "}
              {matrix.criteria.map((c) => c.name).join(", ")}
              {matrixFilters.length > 0 && (
                <>
                  {" "}
                  · {matrixFilters.length} filter{matrixFilters.length === 1 ? "" : "s"}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                navigate(`/matrices/${matrix.id}/edit`);
                setEditing(true);
              }}
              className="text-xs font-medium text-text-secondary transition-colors hover:text-near-black"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(matrix.id)}
              className="text-xs text-text-secondary transition-colors hover:text-error-text"
            >
              Delete
            </button>
          </div>
        </div>
        <div className="mt-2 space-y-1">
          {matrix.criteria.map((c) => (
            <div key={c.name} className="flex items-center gap-2 text-xs">
              <span className="font-medium text-text-focused">{c.name}</span>
              <span className="text-text-secondary">w{c.weight}</span>
              <span className="text-text-secondary">— {c.requirement}</span>
            </div>
          ))}
        </div>
        {matrixFilters.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border-card pt-2">
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">
              Auto-reject filters:
            </span>
            {matrixFilters.map((f) => (
              <span
                key={f.id}
                className="rounded-badge bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
              >
                {f.label}
                {f.type === "age" &&
                  (f.min != null || f.max != null) &&
                  `: ${f.min ?? ""}${f.min != null && f.max != null ? "–" : f.min != null ? "+" : "≤"}${f.max ?? ""}`}
                {(f.type === "gender" ||
                  f.type === "nationality" ||
                  f.type === "location" ||
                  f.type === "work_arrangement") &&
                  f.values.length > 0 &&
                  ` ${f.operator === "in" ? "∈" : "∉"} {${f.values.join(", ")}}`}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-badge border border-rausch bg-white p-4 py-4">
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-text-secondary">Matrix name</label>
          <input
            className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">Employment type</label>
          <div className="mt-1 flex gap-2">
            {(["regular", "contractual", "intern"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setEmpType(t)}
                className={`rounded-btn px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                  empType === t
                    ? "bg-near-black text-white"
                    : "bg-surface-secondary text-text-secondary hover:bg-near-black hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {empType === "intern" && (
            <p className="mt-1 text-[11px] text-amber-700">
              Intern mode: candidates currently pursuing their degree will meet education
              requirements.
            </p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-text-secondary">Criteria</label>
            <span className="text-xs font-medium text-text-secondary">
              Total weight: {totalWeight}
            </span>
          </div>
          <div className="mt-2 space-y-2">
            {criteria.map((c, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 rounded-btn border border-border-card bg-surface-secondary p-2"
              >
                <input
                  className="col-span-3 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  placeholder="Criterion"
                  value={c.name}
                  onChange={(e) =>
                    setCriteria(criteria.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                  }
                />
                <input
                  type="number"
                  min={0}
                  className="col-span-2 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  placeholder="Weight"
                  value={c.weight}
                  onChange={(e) =>
                    setCriteria(
                      criteria.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) } : x))
                    )
                  }
                />
                <input
                  className="col-span-6 rounded-btn border border-border-card bg-white px-2 py-1 text-sm text-near-black focus:outline-none focus:shadow-focus"
                  placeholder="Requirement"
                  value={c.requirement}
                  onChange={(e) =>
                    setCriteria(
                      criteria.map((x, j) => (j === i ? { ...x, requirement: e.target.value } : x))
                    )
                  }
                />
                <button
                  className="col-span-1 rounded-full text-sm text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
                  onClick={() => setCriteria(criteria.filter((_, j) => j !== i))}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            className="mt-2 text-sm font-medium text-rausch hover:underline"
            onClick={() => setCriteria([...criteria, { name: "", weight: 10, requirement: "" }])}
          >
            + Add criterion
          </button>
        </div>

        <div>
          <label className="text-xs font-semibold text-text-secondary">
            Filter requirements (auto-reject)
          </label>
          <div className="mt-2">
            <FilterEditor filters={filters} onChange={setFilters} />
          </div>
        </div>

        {error && <p className="text-sm font-medium text-error-text">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            onClick={cancel}
            disabled={saving}
            className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            disabled={saving}
            className="rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:bg-border-card disabled:text-text-secondary"
            onClick={save}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
